import {
  AppBar,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import {
  Column,
  Columns,
  ExitTransitionPlaceholder,
  Image,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useEffect, useState } from 'react';
import { USA_STATES } from 'shared/form/USAState';
import { LocalStore } from 'shared/helpers/Store';
import {
  createSearchCriteria,
  postingSearchCriteriaVenueSchema,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { useLocationParamsLegacy } from 'shared/routing/LocationParamsLegacy';
import styled from 'styled-components';
import { postingsPageParamsSchema } from '../data/dto/PostingsPageParamsDTO';
import stateSelectionDarkImage from './assets/state-selection-dark.svg';
import stateSelectionImage from './assets/state-selection.svg';
import { trackLoadboardEvent } from './LoadboardAnalytics';

const SelectionDialogContent = styled(DialogContent)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 700px;
  max-height: calc(100% - 144px);
  padding: 24px;
`;

const SelectionBox = styled.div`
  max-width: 432px;
`;

const Illustration = styled(Image)`
  width: 126px;
`;

interface IntrastateCarriersStateSelectionDialogProps {
  open: boolean;
  onClose: () => void;
}

type USAStateOptions = Array<(typeof USA_STATES)[number]>;

const INTRASTATE_CARRIER_SELECTED_STATE = 'intrastate_carrier_selected_state';

function getSelectedState(): USAStateOptions[number] | null {
  const state = LocalStore.get(INTRASTATE_CARRIER_SELECTED_STATE);
  if (!state) return null;

  return USA_STATES.find((s) => s.abbreviation === state) ?? null;
}

function IntrastateCarriersStateSelectionDialogContent({
  onClose,
}: IntrastateCarriersStateSelectionDialogProps) {
  const isMobile = useResponsiveValue(true, false);
  const [value, setValue] = useState<USAStateOptions[number] | null>(
    getSelectedState,
  );
  const [, updateParams] = useLocationParamsLegacy({
    yupSchema: postingsPageParamsSchema,
  });

  function onSearchUpdate() {
    if (!value) return;

    updateParams((prev) => ({
      ...prev,
      current_page: 0,
      utm_content: 'Intrastate State Selection Dialog',
      search_criteria: createSearchCriteria({
        ...prev.search_criteria,
        pickup_radius: null,
        delivery_radius: null,
        latr_waypoints: undefined,
        pickup_venues: [
          postingSearchCriteriaVenueSchema.cast({
            state: value.abbreviation.toLowerCase(),
          }),
        ],
        delivery_venues: [
          postingSearchCriteriaVenueSchema.cast({
            state: value.abbreviation.toLowerCase(),
          }),
        ],
      }),
    }));

    trackLoadboardEvent({
      name: 'Carrier Updated Search on State Selection Dialog for Intrastate Carriers',
      selected_state: `${value.name} (${value.abbreviation}), USA`,
    });

    LocalStore.set(INTRASTATE_CARRIER_SELECTED_STATE, value.abbreviation);
    onClose();
  }

  useEffect(() => {
    trackLoadboardEvent({
      name: 'Carrier Viewed State Selection Dialog for Intrastate Carriers',
    });
  }, []);

  return (
    <>
      <AppBar>
        <Toolbar>
          <Columns align="center" space="small">
            <Column>
              <Typography variant="h3">This load cannot be booked</Typography>
            </Column>

            <Column width="content">
              <IconButton
                aria-label="close posting modal"
                size="small"
                onClick={onClose}
              >
                <Close />
              </IconButton>
            </Column>
          </Columns>
        </Toolbar>
      </AppBar>

      <SelectionDialogContent>
        <SelectionBox>
          <Stack align="center" space="xlarge">
            <Illustration
              alt="state selection illustration"
              src={stateSelectionImage}
              srcDark={stateSelectionDarkImage}
            />
            <Stack space="small">
              <Typography align="center" variant="h2">
                This load requires Interstate Operating Authority
              </Typography>
              <Typography color="textSecondary" align="center">
                It is not available for intrastate carriers. please select your
                state of operation to update your search.
              </Typography>
            </Stack>
            <Stack space="medium" align="center">
              <Box maxWidth="320px" width="100%">
                <Stack space="xxsmall">
                  <Typography>Your operating state</Typography>
                  <Autocomplete
                    fullWidth={true}
                    value={value}
                    onChange={(_, selectedValue: USAStateOptions[number]) => {
                      setValue(selectedValue);
                    }}
                    options={USA_STATES as unknown as USAStateOptions}
                    getOptionLabel={(option) => `${option.abbreviation}, USA`}
                    filterOptions={(filterOptions, queryState) => {
                      const queryValue = queryState.inputValue.toLowerCase();
                      return filterOptions.filter(
                        (state) =>
                          state.name.toLowerCase().includes(queryValue) ||
                          state.abbreviation.toLowerCase().includes(queryValue),
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: 'Select a state',
                          autoComplete: 'new-password',
                        }}
                      />
                    )}
                  />
                </Stack>
              </Box>
              <Button
                onClick={onSearchUpdate}
                size={isMobile ? 'medium' : 'large'}
                disabled={!value}
              >
                Update Search
              </Button>
            </Stack>
          </Stack>
        </SelectionBox>
      </SelectionDialogContent>
    </>
  );
}

export function IntrastateCarriersStateSelectionDialog({
  open,
  onClose,
}: IntrastateCarriersStateSelectionDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth={true}>
      <ExitTransitionPlaceholder in={open}>
        {open && (
          <IntrastateCarriersStateSelectionDialogContent
            open={open}
            onClose={onClose}
          />
        )}
      </ExitTransitionPlaceholder>
    </Dialog>
  );
}
